import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { SelectInput, type OptionsGroup } from "components/inputs/select-input";
import { useLandlordSettingsProvider } from "contexts/landlord-settings-provider";
import { landlordPropertySelector } from "modules/landlord-portal/models/maintenance";

const ALL_PROPERTIES = "all";

export function PropertySelector() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { property_id } = Object.fromEntries(searchParams.entries());
  const { ownershipId } = useLandlordSettingsProvider();

  const { data: propertySelector } = useQuery(
    landlordPropertySelector.queries.read(ownershipId),
  );

  const properties = propertySelector?.data;

  // reset the filter when property ownership change
  useEffect(() => {
    if (!property_id || !properties) return;
    if (properties.some((property) => property.id === property_id)) return;

    setSearchParams(
      (params) => {
        params.delete("property_id");
        return params;
      },
      { replace: true },
    );
  }, [property_id, properties, setSearchParams]);

  const options: OptionsGroup[] = [
    {
      label: { id: "properties", value: "Properties" },
      options: [{ value: ALL_PROPERTIES, label: "All properties" }].concat(
        propertySelector?.data?.map((property) => ({
          value: property.id,
          label: property.display_name,
        })) || [],
      ),
    },
  ];

  return (
    <SelectInput
      menuButtonProps={{
        borderWidth: 1,
        borderColor: "gray.200",
        _hover: { bg: "gray.200" },
      }}
      optionGroups={options}
      value={
        options[0].options.find(
          (o) => o.value === (property_id || ALL_PROPERTIES),
        )?.value ?? ALL_PROPERTIES
      }
      onChange={(v) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (v !== ALL_PROPERTIES) {
          newSearchParams.set("property_id", v);
        } else {
          newSearchParams.delete("property_id");
        }
        setSearchParams(newSearchParams);
      }}
    />
  );
}
